// src/main/shell/tree-search/engine.ts
// Generic command registry parser + matcher + syntax-highlight tokenizer for a
// tree/list search box. Ported VERBATIM (in logic) from the symbol grid's
// searchCommands.ts, with ONE generalization: it is generic over the item type
// `T` and a host-supplied context type `C`, and the command list is PASSED IN
// rather than held in a module-global registry. Everything else — /commands,
// | (OR), & (AND), != (NOT), multi-word values, syntax highlighting — is
// identical to the grid's implementation.
//
// Supports: /commands, | (OR), & (AND), != (NOT), multi-word values
// Get command by name (looked up in the passed-in command list)
export function getCommand(name, commands) {
    return commands.find((c) => c.name === name.toLowerCase());
}
// Get matching commands for autocomplete (over the passed-in command list)
export function getMatchingCommands(partial, commands) {
    const lower = partial.toLowerCase();
    return commands.filter((c) => c.name.startsWith(lower));
}
/**
 * Parse a single command token.
 * Supports: /cmd, /cmd=val, /cmd!=val
 * The value may include spaces — the caller handles multi-word collection.
 */
function parseCommandToken(token, commands) {
    if (!token.startsWith('/'))
        return null;
    const withoutSlash = token.slice(1);
    // Check for != (negation) first
    const neqIndex = withoutSlash.indexOf('!=');
    const eqIndex = withoutSlash.indexOf('=');
    let cmdName;
    let value;
    let negated = false;
    if (neqIndex !== -1 && (eqIndex === -1 || neqIndex < eqIndex)) {
        // /cmd!=value
        cmdName = withoutSlash.slice(0, neqIndex);
        value = withoutSlash.slice(neqIndex + 2);
        negated = true;
    }
    else if (eqIndex === -1) {
        cmdName = withoutSlash;
        value = undefined;
    }
    else {
        cmdName = withoutSlash.slice(0, eqIndex);
        value = withoutSlash.slice(eqIndex + 1);
    }
    let command = getCommand(cmdName, commands);
    /*
     * `:` IS A SYNONYM FOR `=`, because it is what people type.
     *
     * `/trigger:voice` used to parse as a command literally named `trigger:voice`, match nothing, and
     * fall through to "still typing" — so the list stayed at every row and the search looked broken
     * rather than wrong. That is the worst failure a filter can have: it is indistinguishable from a
     * filter that matched everything, and there is nothing on screen to correct.
     *
     * Tried SECOND, and only when the `=` reading found no command, so a value that legitimately
     * contains a colon (`/who=gateway:a1b2c3`) is untouched. And only when the name before the colon
     * IS a known command — otherwise a bare `/note:` stays text, which is what it is.
     */
    if (!command) {
        const colon = withoutSlash.indexOf(':');
        if (colon > 0) {
            const alt = getCommand(withoutSlash.slice(0, colon), commands);
            if (alt) {
                command = alt;
                cmdName = withoutSlash.slice(0, colon);
                value = withoutSlash.slice(colon + 1);
                negated = false;
            }
        }
    }
    if (!command)
        return null;
    return { command, value, negated, raw: token };
}
/**
 * Parse a single AND group from an OR-segment string.
 * Handles multi-word values: everything after /cmd= until the next /cmd, |, or &.
 *
 * Example: "/name=cpu usage /type=boolean" →
 *   commands: [{ name, value: "cpu usage" }, { type, value: "boolean" }]
 */
function parseGroup(segment, isLastGroup, commands) {
    const parsedCommands = [];
    const textParts = [];
    let partialCommand;
    // Tokenize with awareness of multi-word values.
    // Strategy: scan left-to-right. When we hit a /cmd= token, collect all
    // subsequent tokens as the value until the next /cmd, &, or end.
    const tokens = segment.split(/\s+/).filter((t) => t.length > 0);
    let i = 0;
    while (i < tokens.length) {
        const token = tokens[i];
        // Skip bare '&' — explicit AND separator
        if (token === '&') {
            i++;
            continue;
        }
        if (token.startsWith('/')) {
            const parsed = parseCommandToken(token, commands);
            if (parsed) {
                // If the command has a value (has = or !=), collect trailing words as part of the value
                if (parsed.value !== undefined) {
                    const trailingWords = [];
                    // Peek ahead: gather tokens that aren't commands, operators, or separators
                    let j = i + 1;
                    while (j < tokens.length) {
                        const next = tokens[j];
                        if (next === '&' || next.startsWith('/'))
                            break;
                        trailingWords.push(next);
                        j++;
                    }
                    if (trailingWords.length > 0) {
                        parsed.value = (parsed.value + ' ' + trailingWords.join(' ')).trim();
                        parsed.raw = tokens.slice(i, j).join(' ');
                    }
                    i = j;
                }
                else {
                    i++;
                }
                parsedCommands.push(parsed);
            }
            else {
                // Partial command — only treat as partial if it's the very last token of the last group
                if (isLastGroup && i === tokens.length - 1) {
                    partialCommand = token.slice(1);
                }
                else {
                    textParts.push(token);
                }
                i++;
            }
        }
        else {
            textParts.push(token);
            i++;
        }
    }
    const textQuery = textParts.join(' ') || undefined;
    return { group: { textQuery, commands: parsedCommands }, partialCommand };
}
// Parse full search input - supports composable commands with | (OR) and & / space (AND)
export function parseSearchInput(input, commands) {
    const trimmed = input.trim();
    if (!trimmed) {
        return { mode: 'text', textQuery: '', commands: [], rawInput: input, groups: [] };
    }
    // Split by | for OR groups (preserve | positions)
    const orSegments = trimmed.split(/\s*\|\s*/);
    const groups = [];
    let partialCommand;
    for (let g = 0; g < orSegments.length; g++) {
        const segment = orSegments[g].trim();
        if (!segment)
            continue;
        const isLastGroup = g === orSegments.length - 1;
        const result = parseGroup(segment, isLastGroup, commands);
        // Only add non-empty groups
        if (result.group.textQuery || result.group.commands.length > 0) {
            groups.push(result.group);
        }
        if (result.partialCommand !== undefined) {
            partialCommand = result.partialCommand;
        }
    }
    // For backward compat: flatten first group into top-level fields
    const firstGroup = groups[0];
    const textQuery = firstGroup?.textQuery || '';
    const flatCommands = firstGroup?.commands || [];
    // Determine mode
    if (partialCommand !== undefined) {
        return { mode: 'partial', textQuery, commands: flatCommands, partialCommand, rawInput: input, groups };
    }
    if (flatCommands.length > 0 || groups.length > 1) {
        return { mode: 'command', textQuery, commands: flatCommands, rawInput: input, groups };
    }
    return { mode: 'text', textQuery, commands: [], rawInput: input, groups };
}
/**
 * Check if an item matches a single AND group.
 * All conditions within the group must be satisfied.
 *
 * The free-text query match is delegated to the passed-in `textMatch` fn — the
 * generic engine has no knowledge of which item fields are searchable.
 */
function matchesGroup(item, group, ctx, textMatch) {
    // Apply text filter if present
    if (group.textQuery) {
        if (!textMatch(item, group.textQuery, ctx))
            return false;
    }
    // Apply command filters (all must match within a group = AND)
    for (const cmd of group.commands) {
        const result = cmd.command.match(item, cmd.value, ctx);
        // Flip result if negated (!=)
        if (cmd.negated ? result : !result) {
            return false;
        }
    }
    return true;
}
/**
 * Apply filters to an item.
 * Groups are OR'd — an item matches if ANY group matches.
 * Within each group, conditions are AND'd.
 *
 * The free-text query match is delegated to the passed-in `textMatch` fn.
 */
export function matchesFilters(item, parsed, ctx, textMatch) {
    // If we have OR groups, use them
    if (parsed.groups.length > 0) {
        return parsed.groups.some((group) => matchesGroup(item, group, ctx, textMatch));
    }
    // Fallback: use top-level textQuery + commands (single implicit group)
    return matchesGroup(item, { textQuery: parsed.textQuery, commands: parsed.commands }, ctx, textMatch);
}
/**
 * Format search input for syntax highlighting.
 * Handles multi-word values: everything after /cmd= until the next /cmd, |, or & gets 'value' type.
 *
 * "Is this /word a valid command" is decided by looking in the passed-in
 * `commands` array.
 */
export function formatSearchInput(input, commands) {
    const tokens = [];
    const parts = input.split(/(\s+)/);
    let inValue = false; // Are we collecting multi-word value tokens?
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (/^\s+$/.test(part)) {
            tokens.push({ text: part, type: 'text' });
            continue;
        }
        // Operators end multi-word value mode
        if (part === '|' || part === '&') {
            inValue = false;
            tokens.push({ text: part, type: 'operator' });
            continue;
        }
        // A new /command ends multi-word value mode
        if (part.startsWith('/')) {
            inValue = false;
            // Check for != or =
            const neqIndex = part.indexOf('!=');
            const eqIndex = part.indexOf('=');
            if (neqIndex !== -1 && (eqIndex === -1 || neqIndex < eqIndex)) {
                // /cmd!=value
                const cmdName = part.slice(1, neqIndex);
                const isValid = getCommand(cmdName, commands) !== undefined;
                tokens.push({ text: part.slice(0, neqIndex), type: isValid ? 'command' : 'partial' });
                tokens.push({ text: '!=', type: 'operator' });
                const val = part.slice(neqIndex + 2);
                if (val) {
                    tokens.push({ text: val, type: 'value' });
                    inValue = true; // Subsequent non-command tokens are part of this value
                }
            }
            else if (eqIndex === -1) {
                /*
                 * A COLON IS A SEPARATOR HERE TOO, on the same terms as in `parseCommandToken`.
                 *
                 * The colon was added there and not here, so `/trigger:chat` FILTERED correctly while the
                 * highlight painted it yellow — the colour that means "not a command yet". The grid did one
                 * thing and the box above it said another, which is worse than either behaviour alone: the
                 * reader believes the box.
                 *
                 * Same guard as the parser: only when the name before the colon is a KNOWN command, so
                 * `/who=gateway:a1b2c3` keeps its colon and a bare `/note:` stays text.
                 */
                const colonIndex = part.indexOf(':');
                const colonCmd = colonIndex > 1 ? getCommand(part.slice(1, colonIndex), commands) : undefined;
                if (colonCmd) {
                    // The separator rides on the COMMAND token, exactly as `=` does above — two tokens, not
                    // three. Emitting the colon separately would colour it as an operator and make the same
                    // query look different depending on which separator was typed.
                    tokens.push({ text: part.slice(0, colonIndex + 1), type: 'command' });
                    const val = part.slice(colonIndex + 1);
                    if (val) {
                        tokens.push({ text: val, type: 'value' });
                        inValue = true;
                    }
                }
                else {
                    const cmdName = part.slice(1);
                    const isValid = getCommand(cmdName, commands) !== undefined;
                    tokens.push({ text: part, type: isValid ? 'command' : 'partial' });
                }
            }
            else {
                // /cmd=value
                const cmdName = part.slice(1, eqIndex);
                const isValid = getCommand(cmdName, commands) !== undefined;
                tokens.push({ text: part.slice(0, eqIndex + 1), type: isValid ? 'command' : 'partial' });
                const val = part.slice(eqIndex + 1);
                if (val) {
                    tokens.push({ text: val, type: 'value' });
                    inValue = true; // Subsequent non-command tokens are part of this value
                }
            }
            continue;
        }
        // Regular word
        if (inValue) {
            // Continuation of a multi-word value
            tokens.push({ text: part, type: 'value' });
        }
        else {
            tokens.push({ text: part, type: 'text' });
        }
    }
    return tokens;
}
//# sourceMappingURL=engine.js.map